import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { AbstractCRUD, BannerNotificationsService, CRUDService, ExceptionService, PaginatedList, UtilsService } from '../../xform-compat';
import { AuditResource, TradeRestriction } from '../../shared/models';
import { catchError } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { DateTime } from 'luxon';
import { CONFIG } from 'src/app/config';

@Injectable({
  providedIn: 'root'
})
export class RestrictionService extends AbstractCRUD<TradeRestriction> implements CRUDService<TradeRestriction> {

  constructor(
    exceptionService: ExceptionService,
    http: HttpClient,
    utilsService: UtilsService,
    private bannerNotificationService: BannerNotificationsService,
    private translateService: TranslateService
  ) {
    super(exceptionService, http, utilsService);
  }

  public getResourcePath(): string {
    return '/restricted-list/restrictions';
  }

  public getRestrictions(filter: any): Observable<PaginatedList<TradeRestriction>> {
    return super.getHTTP().get(`${this.getApiPath()}/all`, {params: filter})
      .pipe(catchError(ex => super.getException().catchBadResponse(ex)));
  }

  public createRestriction(body: any): Observable<TradeRestriction> {
    return super.getHTTP().post(`${this.getApiPath()}`, body)
      .pipe(catchError(ex => super.getException().catchBadResponse(ex)));
  }

  public updateRestriction(id: number, body: any): Observable<TradeRestriction> {
    return super.getHTTP().put(`${this.getApiPath()}/${id}`, body)
      .pipe(catchError(ex => super.getException().catchBadResponse(ex)));
  }

  public updateRestrictionEndDate(id: number, body: any): Observable<TradeRestriction> {
    return super.getHTTP().put(`${this.getApiPath()}/${id}/end-date`, body)
      .pipe(catchError(ex => super.getException().catchBadResponse(ex)));
  }

  public deleteRestriction(id: number, reason: string, endDate?: DateTime, forceEzeError = false): Observable<TradeRestriction> {
    return super.getHTTP().put(`${this.getApiPath()}/delete/${id}`, {
      endReason: reason,
      endDate: endDate.toUTC().toFormat(CONFIG.dateTimeServerFormatFullMillis),
      forceEzeError
    }).pipe(catchError(ex => super.getException().catchBadResponse(ex)));
  }

  public getRestrictionAudit(body: any): Observable<PaginatedList<AuditResource>> {
    return super.getHTTP().get(`${this.getApiPath()}/audit`, {params: body})
      .pipe(catchError(ex => super.getException().catchBadResponse(ex)));
  }

  public exportRestrictions(filter: any) {
    filter.page = 0;
    filter.pagesize = 9999999;
    return super.getHTTP().get(`${this.getApiPath()}/export`, {params: filter, responseType: 'text'})
      .pipe(catchError(ex => super.getException().catchBadResponse(ex)))
      .subscribe((value: any) => {
        const hiddenElement = document.createElement('a');
        hiddenElement.href = 'data:attachment/csv,' + encodeURIComponent(value);
        hiddenElement.target = '_blank';
        hiddenElement.download = 'trade_restrictions.csv';
        document.body.appendChild(hiddenElement);
        hiddenElement.click();
        this.bannerNotificationService.success(this.translateService.instant('GENERICS.DOWNLOADING'));
        }, () => this.bannerNotificationService.error(this.translateService.instant('GENERICS.DOWNLOAD_ERROR'))
      );
  }

  public exportRestrictionAudit(filter: any) {
    filter.page = 0;
    filter.pagesize = 9999999;
    return super.getHTTP().get(`${this.getApiPath()}/audit/export`, {params: filter, responseType: 'text'})
      .pipe(catchError(ex => super.getException().catchBadResponse(ex)))
      .subscribe((value: any) => {
          const hiddenElement = document.createElement('a');
          hiddenElement.href = 'data:attachment/csv,' + encodeURIComponent(value);
          hiddenElement.target = '_blank';
          hiddenElement.download = 'trade_restriction_audit.csv';
          document.body.appendChild(hiddenElement);
          hiddenElement.click();
          this.bannerNotificationService.success(this.translateService.instant('GENERICS.DOWNLOADING'));
        }, () => this.bannerNotificationService.error(this.translateService.instant('GENERICS.DOWNLOAD_ERROR'))
      );
  }

  public getCompanyNetLevels(companyIds: Array<number>) {
    let httpParams = new HttpParams();
    companyIds.forEach(id => {
      httpParams = httpParams.append('companyId', String(id));
    });
    return super.getHTTP().get(`${this.getApiPath()}/companyNetLevel`, { params: httpParams });
  }
}
